import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface ErrorState {
  id: string;
  message: string;
  severity: AlertColor;
  action?: () => void;
  actionLabel?: string;
  autoHideDuration?: number;
}

interface ErrorContextType {
  showError: (message: string, severity?: AlertColor, action?: () => void, actionLabel?: string) => void;
  showSuccess: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  clearErrors: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<ErrorState[]>([]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const showError = (
    message: string, 
    severity: AlertColor = 'error', 
    action?: () => void, 
    actionLabel?: string
  ) => {
    const id = generateId();
    const newError: ErrorState = {
      id,
      message,
      severity,
      action,
      actionLabel,
      autoHideDuration: severity === 'error' ? 6000 : 4000
    };
    
    setErrors(prev => [...prev, newError]);
    
    // Auto-remove after specified duration
    setTimeout(() => {
      setErrors(prev => prev.filter(error => error.id !== id));
    }, newError.autoHideDuration);
  };

  const showSuccess = (message: string) => {
    showError(message, 'success');
  };

  const showWarning = (message: string) => {
    showError(message, 'warning');
  };

  const showInfo = (message: string) => {
    showError(message, 'info');
  };

  const clearErrors = () => {
    setErrors([]);
  };

  const handleClose = (id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  };

  const value = {
    showError,
    showSuccess,
    showWarning,
    showInfo,
    clearErrors
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
      
      {/* Render error snackbars */}
      {errors.map((error, index) => (
        <Snackbar
          key={error.id}
          open={true}
          autoHideDuration={error.autoHideDuration}
          onClose={() => handleClose(error.id)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ 
            bottom: `${(index * 70) + 24}px !important`,
            zIndex: 9999
          }}
        >
          <Alert 
            severity={error.severity}
            onClose={() => handleClose(error.id)}
            action={
              error.action && error.actionLabel ? (
                <IconButton
                  size="small"
                  aria-label="action"
                  color="inherit"
                  onClick={() => {
                    error.action?.();
                    handleClose(error.id);
                  }}
                  sx={{ ml: 1 }}
                >
                  {error.actionLabel}
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => handleClose(error.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }
            sx={{ 
              width: '100%',
              minWidth: 300,
              maxWidth: 500
            }}
          >
            {error.message}
          </Alert>
        </Snackbar>
      ))}
    </ErrorContext.Provider>
  );
}; 